import type { AvatarProps } from '@/components/ui/avatar';
import { Avatar } from '@/components/ui/avatar';
import { useCreatorProfileImageQuery } from '@/hooks/queries/use-creator-profile-image-query';
import { Flex, Link, Text, VStack } from '@chakra-ui/react';
import { SkeletonCircle } from '@/components/ui/skeleton';
import React from 'react';

// Avatar size to pixel mapping, so the loading placeholder is exactly the size
// of the avatar it stands in for. Mirrors the `--avatar-size` of each size in
// Chakra v3's avatar recipe (sizes.6, 8, 9, 10, 11, 12, 16).
const AVATAR_SIZE_MAP: Record<string, string> = {
  '2xs': '24px',
  xs: '32px',
  sm: '36px',
  md: '40px',
  lg: '44px',
  xl: '48px',
  '2xl': '64px',
};

// Creator identity block (name over handle), as the tables show it.
const CREATOR_NAME_TEXT_PROPS = {
  fontFamily: 'heading',
  fontSize: 'sm',
  fontWeight: 'semibold',
  color: 'ink',
} as const;

const CREATOR_HANDLE_TEXT_PROPS = {
  fontSize: '12.5px',
  color: 'primary.400',
} as const;

const FADED_TEXT_MASK = 'linear-gradient(to right, black calc(100% - 24px), transparent 100%)';
const OVERFLOW_EPSILON_PX = 1;

const singleLineProps = {
  display: 'block',
  minW: 0,
  maxW: 'full',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
} as const;

const useTextOverflow = (content: React.ReactNode) => {
  const ref = React.useRef<HTMLElement | null>(null);
  const [isOverflowing, setIsOverflowing] = React.useState(false);

  React.useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const updateOverflowState = () => {
      const nextIsOverflowing = element.scrollWidth - element.clientWidth > OVERFLOW_EPSILON_PX;
      setIsOverflowing((current) => (current === nextIsOverflowing ? current : nextIsOverflowing));
    };

    updateOverflowState();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateOverflowState);
      return () => window.removeEventListener('resize', updateOverflowState);
    }

    const resizeObserver = new ResizeObserver(updateOverflowState);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [content]);

  return { ref, isOverflowing };
};

type TextProps = React.ComponentProps<typeof Text>;
type LinkProps = React.ComponentProps<typeof Link>;

interface FadedTextProps {
  children: React.ReactNode;
  color?: TextProps['color'];
  cursor?: TextProps['cursor'];
  fontFamily?: TextProps['fontFamily'];
  fontSize?: TextProps['fontSize'];
  fontWeight?: TextProps['fontWeight'];
  href?: LinkProps['href'];
  lineHeight?: TextProps['lineHeight'];
  maxW?: TextProps['maxW'];
  rel?: LinkProps['rel'];
  target?: LinkProps['target'];
  onClick?: React.MouseEventHandler<HTMLElement>;
  _hover?: TextProps['_hover'];
}

const FadedText = ({
  children,
  color,
  cursor,
  fontFamily,
  fontSize,
  fontWeight,
  href,
  lineHeight,
  maxW,
  rel,
  target,
  onClick,
  _hover,
}: FadedTextProps) => {
  const { ref, isOverflowing } = useTextOverflow(children);
  const setTextRef = React.useCallback(
    (node: HTMLElement | null) => {
      ref.current = node;
    },
    [ref]
  );
  const fadeCss = isOverflowing
    ? {
        WebkitMaskImage: FADED_TEXT_MASK,
        maskImage: FADED_TEXT_MASK,
      }
    : undefined;

  if (href) {
    return (
      <Link
        ref={setTextRef}
        color={color}
        cursor={cursor}
        fontFamily={fontFamily}
        fontSize={fontSize}
        fontWeight={fontWeight}
        href={href}
        lineHeight={lineHeight}
        onClick={onClick as LinkProps['onClick']}
        rel={rel}
        target={target}
        _hover={_hover}
        css={fadeCss}
        {...singleLineProps}
        maxW={maxW ?? singleLineProps.maxW}
      >
        {children}
      </Link>
    );
  }

  return (
    <Text
      ref={setTextRef}
      color={color}
      cursor={cursor}
      fontFamily={fontFamily}
      fontSize={fontSize}
      fontWeight={fontWeight}
      lineHeight={lineHeight}
      onClick={onClick as TextProps['onClick']}
      _hover={_hover}
      css={fadeCss}
      {...singleLineProps}
      maxW={maxW ?? singleLineProps.maxW}
    >
      {children}
    </Text>
  );
};

interface CreatorAvatarProps extends Omit<AvatarProps, 'src'> {
  creatorId?: string;
  creatorName?: string;
  fallbackSrc?: string;
  username?: string;
  profileUrl?: string;
  /** Show name and username alongside the avatar */
  showDetails?: boolean;
  /** Click handler for the name (makes name clickable) */
  onNameClick?: () => void;
  /**
   * Lazy fetch mode: try fallbackSrc first, only fetch from API if image fails.
   * Useful for wishlist items with stored URLs that may be expired.
   */
  lazyFetch?: boolean;
}

interface CreatorUsernameLinkProps {
  username: string;
  profileUrl?: string;
  maxW?: TextProps['maxW'];
  onClick?: React.MouseEventHandler<HTMLElement>;
  textProps?: Pick<TextProps, 'color' | 'fontSize' | 'fontWeight' | 'lineHeight' | 'maxW'>;
  _hover?: LinkProps['_hover'];
}

const formatUsername = (username: string) => {
  const cleanUsername = username.trim();

  return cleanUsername.startsWith('@') ? cleanUsername : `@${cleanUsername}`;
};

export const CreatorUsernameLink: React.FC<CreatorUsernameLinkProps> = ({
  username,
  profileUrl,
  maxW,
  onClick,
  textProps,
  _hover,
}) => {
  const label = formatUsername(username);

  if (profileUrl) {
    return (
      <FadedText
        color="primary.focusRing"
        href={profileUrl}
        maxW={maxW}
        onClick={onClick}
        rel="noopener noreferrer"
        fontSize="sm"
        target="_blank"
        _hover={_hover}
      >
        {label}
      </FadedText>
    );
  }

  return (
    <FadedText
      color={textProps?.color ?? 'gray.500'}
      fontSize={textProps?.fontSize ?? 'sm'}
      fontWeight={textProps?.fontWeight}
      lineHeight={textProps?.lineHeight}
      maxW={textProps?.maxW ?? maxW}
      onClick={onClick}
      _hover={_hover}
    >
      {label}
    </FadedText>
  );
};

/**
 * Avatar component that automatically fetches and refreshes creator profile images
 * Handles expired Instagram/Facebook CDN URLs by fetching through the backend endpoint
 * Can optionally display the name and username as a clickable link to the platform profile
 */
const CreatorAvatarContent: React.FC<CreatorAvatarProps> = ({
  creatorId,
  creatorName,
  fallbackSrc,
  username,
  profileUrl,
  showDetails = false,
  onNameClick,
  lazyFetch = false,
  onImageError,
  ...avatarProps
}) => {
  const [imageError, setImageError] = React.useState(false);
  // For lazyFetch: tracks if we should fetch from API after fallback image failed
  const [shouldRefetch, setShouldRefetch] = React.useState(false);
  const canRefresh = Boolean(creatorId);

  const { data: profileImageData, isLoading } = useCreatorProfileImageQuery({
    creatorId,
    // For lazyFetch: only fetch after the stored URL fails to load
    enabled: canRefresh && !imageError && (lazyFetch ? shouldRefetch : true),
  });

  // Determine which URL to use:
  // - lazyFetch mode: use fallbackSrc first, then API result after refetch
  // - normal mode: prefer API result, fallback to stored URL
  const imageUrl =
    lazyFetch && !shouldRefetch ? fallbackSrc : profileImageData?.profileImageUrl || fallbackSrc;

  const handleError: React.ReactEventHandler<HTMLImageElement> = (event) => {
    if (lazyFetch && canRefresh && !shouldRefetch && fallbackSrc) {
      // First failure in lazyFetch mode: stored URL expired, trigger API call
      console.info('Creator avatar stored URL expired, fetching fresh URL:', creatorId);
      setShouldRefetch(true);
    } else {
      // Final failure: show initials
      console.warn('Creator avatar image failed to load:', imageUrl);
      setImageError(true);
    }

    onImageError?.(event);
  };

  // Show skeleton while loading (only when actively fetching)
  if (isLoading && (lazyFetch ? shouldRefetch : true)) {
    const sizeValue = AVATAR_SIZE_MAP[avatarProps.size as string] || AVATAR_SIZE_MAP.md;
    const skeletonWidth = avatarProps.boxSize ?? avatarProps.width ?? avatarProps.w ?? sizeValue;
    const skeletonHeight = avatarProps.boxSize ?? avatarProps.height ?? avatarProps.h ?? sizeValue;
    const skeletonElement = (
      <SkeletonCircle
        width={skeletonWidth}
        height={skeletonHeight}
        flex={avatarProps.flex}
        flexShrink={avatarProps.flexShrink}
      />
    );

    // If no details display is requested, return just the skeleton
    if (!showDetails) {
      return skeletonElement;
    }

    // Show skeleton with placeholder text
    return (
      <Flex align="center" gap={3} minW={0} maxW="full">
        {skeletonElement}
        <VStack alignItems="flex-start" gap={0} flex={1} minW={0} maxW="full">
          <FadedText {...CREATOR_NAME_TEXT_PROPS}>{creatorName || 'Loading...'}</FadedText>
          {username && <FadedText {...CREATOR_HANDLE_TEXT_PROPS}>@{username}</FadedText>}
        </VStack>
      </Flex>
    );
  }

  const avatarElement = (
    <Avatar
      src={!imageError && imageUrl ? imageUrl : undefined}
      name={creatorName || 'Creator'}
      onImageError={handleError}
      colorPalette="primary"
      flexShrink={0}
      {...avatarProps}
    />
  );

  // If no details display is requested, return just the avatar
  if (!showDetails) {
    return avatarElement;
  }

  // Show details: name + username link
  return (
    <Flex align="center" gap={3} minW={0} maxW="full">
      {avatarElement}
      <VStack alignItems="flex-start" gap={0} flex={1} minW={0} maxW="full">
        {onNameClick ? (
          <FadedText
            {...CREATOR_NAME_TEXT_PROPS}
            cursor="pointer"
            _hover={{ textDecoration: 'underline', color: 'primary.500' }}
            onClick={onNameClick}
          >
            {creatorName}
          </FadedText>
        ) : (
          <FadedText {...CREATOR_NAME_TEXT_PROPS}>{creatorName}</FadedText>
        )}
        {username && profileUrl ? (
          <FadedText {...CREATOR_HANDLE_TEXT_PROPS} href={profileUrl} target="_blank">
            @{username}
          </FadedText>
        ) : (
          username && <FadedText {...CREATOR_HANDLE_TEXT_PROPS}>@{username}</FadedText>
        )}
      </VStack>
    </Flex>
  );
};

/**
 * Key the stateful avatar renderer by identity and fallback source. Contact
 * drawers reuse the same component instance while navigating between creators;
 * remounting the inner renderer prevents a failed image/refetch state from
 * leaking into the next creator.
 */
const CreatorAvatar: React.FC<CreatorAvatarProps> = (props) => (
  <CreatorAvatarContent
    key={`${props.creatorId ?? ''}\u0000${props.fallbackSrc ?? ''}`}
    {...props}
  />
);

export default CreatorAvatar;
